import { Injectable, inject } from '@angular/core';
import { forkJoin, map, Observable, of, Subject, switchMap } from 'rxjs';
import { ChartOfAccounts } from '../../../chart-of-accounts/chart-of-accounts.model';
import { ChartOfAccountsService } from '../../../chart-of-accounts/chart-of-accounts.service';
import { Company } from '../../../company/company.model';
import { Customer } from '../../../customer/customer.model';
import { Expense } from '../../../expense/expense.model';
import { ExpenseService } from '../../../expense/expense.service';
import {
  HrEmployeeDocument,
  HrLeaveBalance,
  HrLeaveRequest,
  HrLeaveType,
  HrLearningAssignment,
  HrManagerApprovalItem,
  HrManagerTeamSummary,
  HrNotificationPreference,
  HrPayslip,
  HrSelfServiceProfile,
  HrTimesheet
} from '../../../hr/hr.models';
import { HrService } from '../../../hr/hr.service';
import { SessionStorageService } from '../../../services/session-storage.service';
import {
  MyHrApprovalsWorkspace,
  MyHrExpenseWorkspace,
  MyHrLeaveWorkspace,
  MyHrProfileWorkspace,
  MyHrShellSummary,
  MyHrWorkspaceOverview
} from './my-hr-workspace.models';

@Injectable({ providedIn: 'root' })
export class MyHrWorkspaceService {
  private readonly hrService = inject(HrService);
  private readonly expenseService = inject(ExpenseService);
  private readonly chartOfAccountsService = inject(ChartOfAccountsService);
  private readonly session = inject(SessionStorageService);
  private readonly refreshRequestedSource = new Subject<void>();

  readonly refreshRequested$ = this.refreshRequestedSource.asObservable();

  loadShellSummary(includeSelf: boolean, includeManager: boolean, includeExpenses: boolean, includeExpenseApprovals: boolean): Observable<MyHrShellSummary> {
    return this.loadOverview(includeSelf, includeManager, includeExpenses, includeExpenseApprovals).pipe(
      map(overview => ({
        profile: overview.profile,
        documentsCount: overview.documentsCount,
        leaveCount: overview.leaveCount,
        pendingLeaveCount: overview.pendingLeaveCount,
        timesheetCount: overview.timesheetCount,
        payslipCount: overview.payslipCount,
        learningCount: overview.learningCount,
        approvalsCount: overview.approvalsCount,
        teamCount: overview.teamCount,
        expenseCount: overview.expenseCount,
        pendingExpenseApprovals: overview.pendingExpenseApprovals
      }))
    );
  }

  loadOverview(includeSelf: boolean, includeManager: boolean, includeExpenses: boolean, includeExpenseApprovals: boolean): Observable<MyHrWorkspaceOverview> {
    const companyId = this.companyId;
    const customerId = this.customerId;

    return forkJoin({
      profile: includeSelf ? this.hrService.selfProfile() : of({ data: undefined as HrSelfServiceProfile | undefined }),
      documents: includeSelf ? this.hrService.selfDocuments() : of({ data: [] as HrEmployeeDocument[] }),
      leave: includeSelf ? this.hrService.selfLeave() : of({ data: [] as HrLeaveRequest[] }),
      timesheets: includeSelf ? this.hrService.selfTimesheets() : of({ data: [] as HrTimesheet[] }),
      payslips: includeSelf ? this.hrService.selfPayslips() : of({ data: [] as HrPayslip[] }),
      learning: includeSelf ? this.hrService.selfLearning() : of({ data: [] as HrLearningAssignment[] }),
      managerSummary: includeManager ? this.hrService.managerTeamSummary() : of({ data: undefined }),
      managerApprovals: includeManager ? this.hrService.managerApprovalQueue() : of({ data: [] as HrManagerApprovalItem[] }),
      expenses: includeExpenses && companyId && customerId
        ? this.expenseService.getMyExpenses(companyId, customerId, 0, 50)
        : of({ items: [] as Expense[] }),
      expenseApprovals: includeExpenseApprovals && customerId
        ? this.expenseService.getPendingApprovalsForApprover(customerId)
        : of([] as Expense[])
    }).pipe(
      map(result => {
        const profile = result.profile.data;
        const documents = result.documents.data || [];
        const leave = result.leave.data || [];
        const timesheets = result.timesheets.data || [];
        const payslips = result.payslips.data || [];
        const learning = result.learning.data || [];
        const managerSummary = result.managerSummary.data;
        const managerApprovals = result.managerApprovals.data || [];
        const expenses = result.expenses.items || [];
        const expenseApprovals = Array.isArray(result.expenseApprovals) ? result.expenseApprovals : [];

        return {
          profile,
          documents,
          leave,
          timesheets,
          payslips,
          learning,
          managerSummary,
          managerApprovals,
          expenses,
          expenseApprovals,
          documentsCount: documents.length,
          leaveCount: leave.length,
          pendingLeaveCount: leave.filter(item => ['PENDING', 'SUBMITTED'].includes((item.requestStatus || '').toUpperCase())).length,
          timesheetCount: timesheets.length,
          payslipCount: payslips.length,
          learningCount: learning.length,
          approvalsCount: managerApprovals.length,
          teamCount: managerSummary?.teamSize || 0,
          expenseCount: expenses.length,
          pendingExpenseApprovals: expenseApprovals.length
        };
      })
    );
  }

  loadProfileWorkspace(): Observable<MyHrProfileWorkspace> {
    return forkJoin({
      profile: this.hrService.selfProfile(),
      preferences: this.hrService.notificationPreferences()
    }).pipe(
      map(result => ({
        profile: result.profile.data,
        notificationPreferences: result.preferences.data || []
      }))
    );
  }

  loadDocuments(): Observable<HrEmployeeDocument[]> {
    return this.hrService.selfDocuments().pipe(map(result => result.data || []));
  }

  loadLeaveWorkspace(): Observable<MyHrLeaveWorkspace> {
    return this.hrService.selfProfile().pipe(
      switchMap(profileResult => {
        const profile = profileResult.data;
        const employeeId = profile?.employeeId;
        return forkJoin({
          leaveTypes: this.companyId ? this.hrService.leaveTypes(this.companyId) : of({ data: [] as HrLeaveType[] }),
          leaveBalances: employeeId && this.companyId
            ? this.hrService.leaveBalances(this.companyId, employeeId)
            : of({ data: [] as HrLeaveBalance[] }),
          leaveRequests: this.hrService.selfLeave(),
          profile: of(profile)
        });
      }),
      map(result => ({
        profile: result.profile,
        leaveTypes: result.leaveTypes.data || [],
        leaveBalances: result.leaveBalances.data || [],
        requests: result.leaveRequests.data || []
      }))
    );
  }

  loadTimesheets(): Observable<HrTimesheet[]> {
    return this.hrService.selfTimesheets().pipe(map(result => result.data || []));
  }

  loadPayslips(): Observable<HrPayslip[]> {
    return this.hrService.selfPayslips().pipe(map(result => result.data || []));
  }

  loadLearning(): Observable<HrLearningAssignment[]> {
    return this.hrService.selfLearning().pipe(map(result => result.data || []));
  }

  loadApprovalsWorkspace(includeSelf: boolean, includeManager: boolean, includeExpenseApprovals: boolean): Observable<MyHrApprovalsWorkspace> {
    const companyId = this.companyId;
    const customerId = this.customerId;
    return forkJoin({
      leave: includeSelf ? this.hrService.selfLeave() : of({ data: [] as HrLeaveRequest[] }),
      timesheets: includeSelf ? this.hrService.selfTimesheets() : of({ data: [] as HrTimesheet[] }),
      managerApprovals: includeManager ? this.hrService.managerApprovalQueue() : of({ data: [] as HrManagerApprovalItem[] }),
      expenseApprovals: includeExpenseApprovals && customerId
        ? this.expenseService.getPendingApprovalsForApprover(customerId)
        : of([] as Expense[]),
      expenses: includeSelf && companyId && customerId
        ? this.expenseService.getMyExpenses(companyId, customerId, 0, 25)
        : of({ items: [] as Expense[] })
    }).pipe(
      map(result => ({
        leaveRequests: result.leave.data || [],
        timesheets: result.timesheets.data || [],
        managerApprovals: result.managerApprovals.data || [],
        expenseApprovals: Array.isArray(result.expenseApprovals) ? result.expenseApprovals : [],
        expenses: result.expenses.items || []
      }))
    );
  }

  loadManagerSummary(): Observable<HrManagerTeamSummary | undefined> {
    return this.hrService.managerTeamSummary().pipe(map(result => result.data));
  }

  loadExpensesWorkspace(): Observable<MyHrExpenseWorkspace> {
    const companyId = this.companyId;
    const customerId = this.customerId;
    return forkJoin({
      accounts: companyId ? this.chartOfAccountsService.getAllChartOfAccountsByCompany(companyId, false) : of([] as ChartOfAccounts[]),
      expenses: companyId && customerId ? this.expenseService.getMyExpenses(companyId, customerId, 0, 50) : of({ items: [] as Expense[] })
    }).pipe(
      map(result => ({
        company: this.company,
        customer: this.customer,
        currencyCode: this.currencyCode,
        accounts: this.expenseAccounts(result.accounts || []),
        expenses: result.expenses.items || []
      }))
    );
  }

  saveNotificationPreference(request: HrNotificationPreference): Observable<HrNotificationPreference | undefined> {
    return this.hrService.saveNotificationPreference(request).pipe(map(result => result.data));
  }

  uploadDocument(request: HrEmployeeDocument): Observable<HrEmployeeDocument | undefined> {
    return this.hrService.uploadSelfDocument(request).pipe(map(result => result.data));
  }

  submitLeaveRequest(employeeId: number, request: HrLeaveRequest): Observable<HrLeaveRequest | undefined> {
    return this.hrService.submitLeaveRequest(employeeId, request).pipe(map(result => result.data));
  }

  createExpense(request: Expense, submitForApproval = false): Observable<Expense> {
    return this.expenseService.createExpense(request).pipe(
      switchMap(expense => submitForApproval && expense.expenseId
        ? this.expenseService.submitExpenseForApproval(expense.expenseId)
        : of(expense))
    );
  }

  requestRefresh(): void {
    this.refreshRequestedSource.next();
  }

  get companyId(): number | undefined {
    return this.company?.id;
  }

  get customerId(): number | undefined {
    return this.customer?.id;
  }

  get company(): Company | undefined {
    return this.session.getCompany();
  }

  get customer(): Customer | undefined {
    return this.session.getCustomer();
  }

  get currencyCode(): string {
    const company = this.company;
    const countries = this.session.getCountryList() || [];
    return countries.find(country => country.id === company?.countryId)?.currencyCode || 'SAR';
  }

  private expenseAccounts(accounts: ChartOfAccounts[]): ChartOfAccounts[] {
    const expenseAccounts = accounts.filter(account =>
      account.isActive
      && !account.deprecated
      && !account.childrenAccounts?.length
      && ['expense', 'expenses'].includes((account.internalType || '').toLowerCase())
    );
    if (expenseAccounts.length) {
      return expenseAccounts;
    }
    return accounts.filter(account => account.isActive && !account.deprecated && !account.childrenAccounts?.length);
  }
}
